import type { ReactNode } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import { EmbeddingStatusBadge } from "@/components/common/embedding-status-badge";
import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import {
  EMBEDDING_STATUS_OPTIONS,
  ENRICHMENT_STATUS_OPTIONS,
} from "@/lib/enrichment";

/**
 * The ONE enrichment/embedding list-column definition.
 *
 * **Why a factory and not nine hand-rolled columns that happen to share a
 * badge.** The enrichment column used to exist on 8 of the 9 enriched entities
 * (genres had none) and the embedding column on 1 of the 4 embeddable ones, and
 * the eight copies had already drifted apart on header text, `size`, accessor
 * spelling and the option set they filtered by, in exactly the way the six
 * duplicate colour maps did. Nine definitions that
 * merely *agree today* drift again. Every list calls these two functions.
 *
 * **Coverage is not symmetric, and the asymmetry is a hard constraint:**
 *
 * | column | entities |
 * |---|---|
 * | {@link enrichmentStatusColumn} | all nine — artist, venue, genre, geodata, event, movie, person, show-genre, screening |
 * | {@link embeddingStatusColumn} | **exactly four** — artist, venue, genre, event |
 *
 * `EMBEDDABLE` in Tina (`src/enrichment/types.py`) is precisely those four, the
 * Admin API 400s on `embeddingStatus` for the other five, and `POST
 * /enrichments` rejects `mode=embed` for them outright. Do not add the
 * embedding column to movies/people/show-genres/screenings/geodata.
 *
 * **Position.** Immediately next to the list's existing `status` column, never
 * out on the right beside `actions` — a far-right column scrolls off screen on
 * the wide entity tables, which is how the enrichment lifecycle stayed
 * invisible. Genres, geodata and show-genres have no `status` column; there the
 * columns go in the equivalent early-middle slot.
 */

/** Column id — also the Typesense filter/sort field, so it is not cosmetic. */
export const ENRICHMENT_STATUS_COLUMN_ID = "enrichmentStatus";

/** Column id — also the Typesense filter/sort field, so it is not cosmetic. */
export const EMBEDDING_STATUS_COLUMN_ID = "embeddingStatus";

export interface StatusColumnOptions<TData> {
  /**
   * Header text. Defaults to `"Enrichment"` / `"Embedding"` — the majority
   * spelling across the pre-Phase-4 lists, and short enough that the two read
   * as a pair when they sit side by side.
   */
  header?: string;
  /** Column width. Widen it when {@link StatusColumnOptions.actions} is set. */
  size?: number;
  /**
   * Override how the status is read off a row. Defaults to
   * `row.document.<field>` — the Typesense `IHit` shape every entity list uses.
   */
  accessorFn?: (row: TData) => string | null | undefined;
  /**
   * Extra controls rendered beside the badge — the per-entity `Review` /
   * `Retry` affordances artists, venues and geodata already carried inline.
   * Returning a falsy node renders the bare badge.
   */
  actions?: (context: { status: string | null; row: TData }) => ReactNode;
  /**
   * Whether the column offers a `select` filter. Default `true`.
   *
   * The escape hatch exists because the filter is served by Typesense, not by
   * the document: a column filtering on a field the collection does not index
   * 400s the **whole list**, not just the filter. Turn it off for a collection
   * whose schema PATCH has not landed yet, rather than forking the column.
   */
  filterable?: boolean;
}

function readDocumentStatus(row: unknown, field: string): string | null {
  const document = (row as { document?: Record<string, unknown> } | null)
    ?.document;
  const value = document?.[field];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function statusColumn<TData>(
  field: string,
  defaultHeader: string,
  options: StatusColumnOptions<TData>,
  renderBadge: (status: string | null) => ReactNode,
  filterOptions: ReadonlyArray<{ label: string; value: string }>,
): ColumnDef<TData> {
  const {
    header = defaultHeader,
    size = 130,
    accessorFn,
    actions,
    filterable = true,
  } = options;

  return {
    id: field,
    header,
    accessorFn: (row) =>
      accessorFn ? (accessorFn(row) ?? null) : readDocumentStatus(row, field),
    enableSorting: false,
    size,
    cell: function StatusCell({ getValue, row }) {
      const raw = getValue();
      const status = typeof raw === "string" && raw.length > 0 ? raw : null;
      const extra = actions?.({ status, row: row.original });
      if (!extra) return renderBadge(status);
      return (
        <div className="flex items-center gap-2">
          {renderBadge(status)}
          {extra}
        </div>
      );
    },
    ...(filterable
      ? { meta: { filterType: "select", options: filterOptions } }
      : { enableColumnFilter: false }),
  };
}

/**
 * The `enrichmentStatus` column — every one of the nine enriched entities.
 *
 * Filters through `ENRICHMENT_STATUS_OPTIONS`, never an inline array:
 * `Unavailable` is the resting state of every Tina-created entity, and three
 * lists used to omit it, so the most common value on them was unfilterable.
 */
export function enrichmentStatusColumn<TData>(
  options: StatusColumnOptions<TData> = {},
): ColumnDef<TData> {
  return statusColumn(
    ENRICHMENT_STATUS_COLUMN_ID,
    "Enrichment",
    options,
    (status) => <EnrichmentStatusBadge status={status} />,
    ENRICHMENT_STATUS_OPTIONS,
  );
}

/**
 * The `embeddingStatus` column — artist, venue, genre and event **only**.
 *
 * See the module docstring: the other five entities have no embedding, the
 * Admin API rejects the field for them, and a column over a field that is
 * always absent teaches curators that those entities are perpetually
 * un-embedded rather than not embeddable.
 */
export function embeddingStatusColumn<TData>(
  options: StatusColumnOptions<TData> = {},
): ColumnDef<TData> {
  return statusColumn(
    EMBEDDING_STATUS_COLUMN_ID,
    "Embedding",
    options,
    (status) => <EmbeddingStatusBadge status={status} />,
    EMBEDDING_STATUS_OPTIONS,
  );
}
